import { memo } from "react";
import { Check, Palette, RotateCcw } from "lucide-react";
import { ResumeTemplate, LetterTemplate } from "@/components/docs/Templates";
import { PAGE_W, PAGE_H } from "@/components/docs/PaperPreview";
import { SAMPLE_RESUME, TEMPLATES } from "@/lib/sample";
import { ACCENTS, DEFAULT_ACCENT, accentFor, isLight } from "@/lib/colour";

const THUMB_W = 72;
const SCALE = THUMB_W / PAGE_W;

const SAMPLE_LETTER = {
  sender: { name: SAMPLE_RESUME.name, email: SAMPLE_RESUME.email, phone: SAMPLE_RESUME.phone, location: SAMPLE_RESUME.location },
  company: "Acme", role: "Product Lead",
  content: "Dear Hiring Manager,\n\nI'm excited to apply for this role. Over seven years I have shipped products customers love and grown revenue along the way.\n\nI would welcome the chance to talk.\n\nKind regards,\nAlex Morgan",
};

const Thumb = memo(({ type, template, accent }) => (
  <div className="overflow-hidden rounded-[3px] bg-white shadow-[0_1px_3px_rgba(15,23,42,.15)]" style={{ width: THUMB_W, height: PAGE_H * SCALE }}>
    <div className="pointer-events-none origin-top-left" style={{ width: PAGE_W, transform: `scale(${SCALE})` }} aria-hidden>
      {type === "resume" ? <ResumeTemplate template={template} data={SAMPLE_RESUME} accent={accent} />
        : <LetterTemplate template={template} data={SAMPLE_LETTER} accent={accent} />}
    </div>
  </div>
));

const Swatch = ({ hex, name, active, onPick }) => (
  <button type="button" onClick={() => onPick(hex)} title={name} aria-label={name} data-testid={`accent-swatch-${hex.slice(1)}`}
    className={`flex h-7 w-7 items-center justify-center rounded-full ring-offset-2 ring-offset-paper-subtle transition-transform hover:scale-110 ${active ? "ring-2 ring-ink" : ""}`}
    style={{ background: hex }}>
    {active && <Check className="h-3.5 w-3.5" style={{ color: isLight(hex) ? "#0F172A" : "#FFFFFF" }} />}
  </button>
);

const AccentPicker = ({ template, accent, onChange }) => {
  const current = accentFor(template, accent);
  const custom = !ACCENTS.some((a) => a.hex.toLowerCase() === current.toLowerCase());
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2.5" data-testid="accent-picker">
      <span className="mr-1 font-mono text-[10px] uppercase tracking-widest text-taupe">Colour</span>
      {ACCENTS.map((a) => <Swatch key={a.hex} {...a} active={a.hex.toLowerCase() === current.toLowerCase()} onPick={onChange} />)}
      <label title="Pick any colour" data-testid="accent-custom"
        className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full ring-offset-2 ring-offset-paper-subtle transition-transform hover:scale-110 ${custom ? "ring-2 ring-ink" : ""}`}
        style={{ background: custom ? current : "conic-gradient(#F25F4C,#F2A93B,#1A9E8F,#1E40AF,#6C4AB6,#F25F4C)" }}>
        <Palette className="h-3.5 w-3.5 text-white drop-shadow" />
        <input type="color" value={current} onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0" data-testid="accent-custom-input" />
      </label>
      {accent && accent.toLowerCase() !== DEFAULT_ACCENT[template].toLowerCase() && (
        <button type="button" onClick={() => onChange(DEFAULT_ACCENT[template])} className="ml-1 inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid="accent-reset">
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      )}
    </div>
  );
};

export const StylePicker = ({ type, template, accent, onChange }) => (
  <div className="mx-auto mb-6 max-w-[794px]" data-testid="style-picker">
    <div className="relative">
    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 pr-8 sm:pr-1" role="tablist">
      {TEMPLATES.map((t) => {
        const on = template === t.id;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on} onClick={() => onChange({ template: t.id })} data-testid={`template-${t.id}-btn`}
            className="group flex shrink-0 flex-col items-center gap-1.5">
            <span className={`rounded-md p-1 transition-colors ${on ? "bg-thread" : "bg-transparent group-hover:bg-paper-border"}`}>
              <Thumb type={type} template={t.id} accent={t.id === "bold" || t.id === "modern" ? accent : undefined} />
            </span>
            <span className={`text-[11px] font-semibold ${on ? "text-ink" : "text-ink-soft"}`}>{t.name}</span>
          </button>
        );
      })}
    </div>
    <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-paper-subtle to-transparent sm:hidden" />
    </div>
    {(template === "bold" || template === "modern") && <AccentPicker template={template} accent={accent} onChange={(hex) => onChange({ accent: hex })} />}
  </div>
);
